import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getApiError } from '../lib/utils';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Clock, Sun, Moon, ArrowRight, AlertCircle, Eye, EyeOff } from 'lucide-react';

const formatCountdown = (s) => {
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${m}:${sec.toString().padStart(2, '0')}`
}

const LoginPage = () => {
  const { login, register, ssoError, clearSsoError } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lockoutSeconds, setLockoutSeconds] = useState(0);

  useEffect(() => {
    if (lockoutSeconds <= 0) return;
    const id = setTimeout(() => setLockoutSeconds(s => s - 1), 1000);
    return () => clearTimeout(id);
  }, [lockoutSeconds]);

  useEffect(() => {
    if (ssoError) {
      setError(decodeURIComponent(ssoError.replace(/\+/g, ' ')));
      clearSsoError();
    }
  }, [ssoError, clearSsoError]);

  const handleMicrosoftSSO = () => {
    const frontendUrl = encodeURIComponent(window.location.origin);
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/microsoft?frontend_url=${frontendUrl}`;
  };
  
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    department: 'engineering',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      if (isRegister) {
        await register(formData);
      } else {
        await login(formData.email, formData.password);
      }
    } catch (err) {
      if (err.response?.status === 429) {
        const retryAfter = err.response.headers?.['retry-after'];
        const seconds = retryAfter ? parseInt(retryAfter, 10) : 900;
        setLockoutSeconds(isNaN(seconds) ? 900 : seconds);
        setError('');
      } else {
        setError(getApiError(err, 'Authentication failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Image Background */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758691737492-48e8fdd336f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHx0ZWFtJTIwc3VjY2VzcyUyMGNlbGVicmF0aW9uJTIwYWNjb21wbGlzaG1lbnQlMjBoYXBweSUyMHdvcmtwbGFjZXxlbnwwfHx8fDE3NzI1MjEyMzZ8MA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/70 to-blue-900/80" />
        
        <div className="relative z-10 p-12 flex flex-col justify-between h-full">
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white tracking-tight">WorkIntel</span>
            </div>
            <h1 className="text-5xl font-extrabold text-white leading-tight mt-16">
              Achieve More<br />Together
            </h1>
            <p className="text-white/90 text-lg mt-6 max-w-md">
              Transform how your team tracks time and manages projects. Celebrate every milestone with clarity and purpose.
            </p>
          </div>
          
          <div>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                <div className="text-3xl font-bold text-white mb-1">98%</div>
                <div className="text-xs text-white/80 uppercase tracking-wider">Satisfaction</div>
              </div>
              <div className="p-4 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                <div className="text-3xl font-bold text-white mb-1">2x</div>
                <div className="text-xs text-white/80 uppercase tracking-wider">Productivity</div>
              </div>
              <div className="p-4 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                <div className="text-3xl font-bold text-white mb-1">50+</div>
                <div className="text-xs text-white/80 uppercase tracking-wider">Teams</div>
              </div>
            </div>
            
            {/* Arismeta Branding */}
            <div className="mt-8 flex items-center gap-2 text-white/70 text-sm">
              <span>Created by</span>
              <a href="https://arismeta.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                <img src="assets/logo-white.png" alt="Arismeta" className="h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="flex justify-between items-center mb-8">
            <div className="lg:hidden flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-lg">WorkIntel</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-lg"
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold mb-2">
              {isRegister ? 'Create Account' : 'Welcome back'}
            </h2>
            <p className="text-muted-foreground">
              {isRegister 
                ? 'Register as the first Super Admin' 
                : 'Enter your credentials to access your dashboard'}
            </p>
          </div>

          {lockoutSeconds > 0 && (
            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl dark:bg-orange-500/10 dark:border-orange-500/25" data-testid="lockout-banner">
              <div className="flex items-center gap-2.5 mb-1 text-orange-700 dark:text-orange-300">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm font-semibold">Too many failed attempts</span>
              </div>
              <p className="text-sm text-orange-600 dark:text-orange-400 pl-6">
                Try again in{' '}
                <span className="font-bold tabular-nums">{formatCountdown(lockoutSeconds)}</span>
              </p>
            </div>
          )}

          {error && !lockoutSeconds && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3 text-destructive" data-testid="login-error">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {isRegister && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name" className="text-sm font-medium mb-2 block">First Name</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    required
                    className="h-11 rounded-lg"
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name" className="text-sm font-medium mb-2 block">Last Name</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    required
                    className="h-11 rounded-lg"
                    data-testid="last-name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-sm font-medium mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value.toLowerCase()})}
                required
                placeholder="you@company.com"
                className="h-11 rounded-lg"
                disabled={lockoutSeconds > 0}
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium mb-2 block">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  placeholder="••••••••"
                  className="h-11 rounded-lg pr-10"
                  disabled={lockoutSeconds > 0}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {isRegister && (
              <div>
                <Label htmlFor="department" className="text-sm font-medium mb-2 block">Department</Label>
                <Select
                  value={formData.department}
                  onValueChange={(v) => setFormData({...formData, department: v})}
                >
                  <SelectTrigger className="h-11 rounded-lg" data-testid="department-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="engineering">Software Engineering</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="design">Design</SelectItem>
                    <SelectItem value="project_management">Project Management</SelectItem>
                    <SelectItem value="leadership">Leadership</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium flex items-center justify-center gap-2 rounded-lg shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all duration-200"
              disabled={loading || lockoutSeconds > 0}
              data-testid="submit-button"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              ) : lockoutSeconds > 0 ? (
                <span className="tabular-nums">Locked · {formatCountdown(lockoutSeconds)}</span>
              ) : (
                <>
                  {isRegister ? 'Create Account' : 'Sign In'}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </form>

          {!isRegister && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">or continue with</span>
                </div>
              </div>
              <button
                type="button"
                onClick={handleMicrosoftSSO}
                className="w-full h-11 flex items-center justify-center gap-3 rounded-lg border border-border bg-background hover:bg-muted transition-colors text-sm font-medium"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 21 21">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#00a4ef"/>
                  <rect x="1" y="11" width="9" height="9" fill="#7fba00"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                Sign in with Microsoft
              </button>
            </>
          )}

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {setIsRegister(!isRegister); setError('');}}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isRegister 
                ? 'Already have an account? Sign in' 
                : 'First time? Create Super Admin account'}
            </button>
          </div>
          
          {/* Mobile Arismeta Branding */}
          <div className="lg:hidden mt-8 flex items-center justify-center gap-2 text-muted-foreground text-sm">
            <span>Created by</span>
            <a href="https://arismeta.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-primary transition-colors">
              <span className="font-medium">Arismeta</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
